import { unwrap } from "@sagot/api-client";
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import type { ConversationRun, HookSpec, ManualRunStarted, PathAnalysis, TranslationDraft, VersionDetail, VersionDiff, VersionList } from "./p4-types";
import type { Definition, RunOut, WorkflowDetail } from "./types";
import { workflowKeys } from "./use-workflows";

const HOOK_SPEC_STALE_MS = 10 * 60_000;

/** Query keys of phase 4; all sit under `workflowKeys.all`, so publishing, rolling back or cancelling refreshes them with the rest. */
export const workflowP4Keys = {
  hookSpec: (ws: string) => [...workflowKeys.all(ws), "hook-spec"] as const,
  versions: (ws: string, id: string) => [...workflowKeys.detail(ws, id), "versions"] as const,
  version: (ws: string, id: string, number: number) => [...workflowKeys.detail(ws, id), "versions", number] as const,
  diff: (ws: string, id: string, base: string, target: string) => [...workflowKeys.detail(ws, id), "diff", base, target] as const,
  analysis: (ws: string, id: string, version: number | null, days: number) => [...workflowKeys.detail(ws, id), "analysis", version ?? "all", days] as const,
  conversationRuns: (ws: string, conversationId: string) => ["ws", ws, "conversations", conversationId, "workflow-runs"] as const,
};

/** How a client's system calls a "Wait for webhook" URL (the same for every workspace, so it is cached for a while). */
export function useHookSpec(): UseQueryResult<HookSpec> {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({ queryKey: workflowP4Keys.hookSpec(ws), queryFn: () => unwrap(api.GET("/v1/workflows/hook-spec")), staleTime: HOOK_SPEC_STALE_MS, retry: false });
}

export function useVersions(id: string, enabled: boolean): UseQueryResult<VersionList> {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: workflowP4Keys.versions(ws, id),
    queryFn: () => unwrap(api.GET("/v1/workflows/{workflow_id}/versions", { params: { path: { workflow_id: id } } })),
    enabled,
  });
}

/** One published version's definition (read-only). */
export function useVersion(id: string, number: number | null): UseQueryResult<VersionDetail> {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: workflowP4Keys.version(ws, id, number ?? 0),
    queryFn: () => unwrap(api.GET("/v1/workflows/{workflow_id}/versions/{number}", { params: { path: { workflow_id: id, number: number ?? 0 } } })),
    enabled: number !== null,
  });
}

/** Steps added, removed, changed or moved going from `base` to `target` (a version number, `draft` or `live`). */
export function useVersionDiff(id: string, base: string | null, target: string | null): UseQueryResult<VersionDiff> {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: workflowP4Keys.diff(ws, id, base ?? "", target ?? ""),
    queryFn: () => unwrap(api.GET("/v1/workflows/{workflow_id}/diff", { params: { path: { workflow_id: id }, query: { base: base ?? undefined, target: target ?? undefined } } })),
    enabled: base !== null && target !== null,
  });
}

/** Copies a published version into the draft; the answer is the workflow with that draft, which the builder shows. */
export function useRollback(id: string) {
  const api = useApi();
  const ws = useWorkspaceId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { number: number; force: boolean }) =>
      unwrap(api.POST("/v1/workflows/{workflow_id}/versions/{number}/rollback", { params: { path: { workflow_id: id, number: body.number } }, body: { force: body.force } })),
    onSuccess: (detail: WorkflowDetail) => {
      client.setQueryData(workflowKeys.detail(ws, id), detail);
      return client.invalidateQueries({ queryKey: workflowKeys.all(ws) });
    },
  });
}

/** Per step: runs that reached it, went on, dropped, are waiting or failed (`version` empty: every version). */
export function usePathAnalysis(id: string, version: number | null, days: number, enabled: boolean): UseQueryResult<PathAnalysis> {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: workflowP4Keys.analysis(ws, id, version, days),
    queryFn: () => unwrap(api.GET("/v1/workflows/{workflow_id}/analysis", { params: { path: { workflow_id: id }, query: { days, ...(version === null ? { all_versions: true } : { version }) } } })),
    enabled,
  });
}

interface DraftTranslationsInput {
  language: "en" | "fil";
  /** The definition being edited, so unsaved steps are translated too. */
  definition: Definition;
}

/** Machine-translates the visitor-facing texts that have no translation yet; nothing is saved by the server. */
export function useDraftTranslations(id: string) {
  const api = useApi();
  return useMutation({
    mutationFn: (input: DraftTranslationsInput): Promise<TranslationDraft> =>
      unwrap(api.POST("/v1/workflows/{workflow_id}/translations/draft", { params: { path: { workflow_id: id } }, body: { language: input.language, only_missing: true, definition: { ...input.definition } } })),
  });
}

/** Stops an active run (409 when it already ended). */
export function useCancelRun() {
  const api = useApi();
  const ws = useWorkspaceId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (runId: string): Promise<RunOut> => unwrap(api.POST("/v1/workflow-runs/{run_id}/cancel", { params: { path: { run_id: runId } } })),
    onSettled: () => Promise.all([client.invalidateQueries({ queryKey: workflowKeys.all(ws) }), client.invalidateQueries({ queryKey: ["ws", ws, "conversations"] })]),
  });
}

/** The conversation's workflow runs, newest first. */
export function useConversationRuns(conversationId: string, enabled: boolean): UseQueryResult<ConversationRun[]> {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: workflowP4Keys.conversationRuns(ws, conversationId),
    queryFn: () => unwrap(api.GET("/v1/conversations/{conversation_id}/workflow-runs", { params: { path: { conversation_id: conversationId } } })),
    enabled,
    retry: false,
  });
}

/** Runs a live reusable workflow on the conversation (staff, from the Inbox). */
export function useStartConversationRun(conversationId: string) {
  const api = useApi();
  const ws = useWorkspaceId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (workflowId: string): Promise<ManualRunStarted> =>
      unwrap(api.POST("/v1/conversations/{conversation_id}/workflow-runs", { params: { path: { conversation_id: conversationId } }, body: { workflow_id: workflowId } })),
    onSuccess: () => client.invalidateQueries({ queryKey: workflowP4Keys.conversationRuns(ws, conversationId) }),
  });
}
